import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import { isXpLowVersion } from '@/utils/xpEnv'

/** 从路由 query 读取 appVersion，兼容 appVersion / appversion */
export function getStarAppVersion(
  query: RouteLocationNormalizedLoaded['query'] | Record<string, unknown>
): string | undefined {
  const raw = (query as Record<string, unknown>).appVersion ?? (query as Record<string, unknown>).appversion
  if (raw == null || raw === '') return undefined
  if (Array.isArray(raw)) return String(raw[0])
  return String(raw)
}

export function useStarXpCompat() {
  const route = useRoute()
  const highversion = computed(() => !isXpLowVersion(route.query as Record<string, unknown>))
  const xpTheme = computed(() => (route.query.theme === 'light' ? 'light' : 'dark'))
  return { highversion, xpTheme, route }
}

export function useStarTheme() {
  const route = useRoute()
  const isLight = computed(() => route.query.theme === 'light')

  // 常用色值
  const colors = computed(() => ({
    bg: isLight.value ? '#ffffff' : '#070707',
    bgCard: isLight.value ? '#f0f2f5' : '#181818',
    bgCardSub: isLight.value ? '#e8edf5' : '#1f1f1f',
    bgPanel: isLight.value ? '#ffffff' : '#181818',
    bgTabActive: isLight.value ? '#ffffff' : '#3d3d3d',
    bgTabBar: isLight.value ? '#f0f0f0' : '#1f1f1f',
    bgActionBtn: isLight.value ? '#f0f2f5' : 'rgba(255,255,255,0.07)',
    bgActionBtnHover: isLight.value ? '#e4e8ef' : 'rgba(255,255,255,0.12)',
    bgInput: isLight.value ? '#f5f5f5' : '#2f2e2e',
    textPrimary: isLight.value ? '#1a1a1a' : '#ffffff',
    textSecondary: isLight.value ? '#666666' : 'rgba(255,255,255,0.6)',
    textMuted: isLight.value ? '#999999' : '#7d7d7d',
    textInput: isLight.value ? '#666666' : '#ffffff',
    textTabActive: isLight.value ? '#fe4043' : '#ffffff',
    divider: isLight.value ? '#f0f0f0' : 'rgba(255,255,255,0.08)',
    statDivider: isLight.value ? '#e0e0e0' : 'rgba(255,255,255,0.12)',
    border: isLight.value ? '#e0e0e0' : 'rgba(255,255,255,0.08)',
    borderInput: isLight.value ? '#d9dbdd' : '#3d3d3d',
    accent: '#fe4043',
    rise: '#fe4043',
    fall: '#39e365',
  }))

  const isDark = computed(() => !isLight.value)

  /** 滚动条等主题 CSS 变量，供 StarXpGate 或个别页面 :style 使用 */
  const themeCssVars = computed(() => getStarScrollbarCssVars(isLight.value))

  return { isLight, isDark, colors, themeCssVars }
}

/**
 * 星投滚动条主题 token（黑/白版）。
 * 由 StarXpGate 注入到根节点，子树内所有 overflow 区域自动生效，无需各页重复配置。
 */
export const STAR_SCROLLBAR = {
  width: '6px',
  radius: '2px',
  dark: {
    thumb: 'rgba(255,255,255,0.35)',
    thumbHover: 'rgba(255,255,255,0.55)',
  },
  light: {
    thumb: 'rgba(0,0,0,0.2)',
    thumbHover: 'rgba(0,0,0,0.35)',
  },
} as const

/** 星投模块滚动条 CSS 变量 */
export function getStarScrollbarCssVars(isLight: boolean): Record<string, string> {
  const palette = isLight ? STAR_SCROLLBAR.light : STAR_SCROLLBAR.dark
  return {
    '--star-scrollbar-width': STAR_SCROLLBAR.width,
    '--star-scrollbar-radius': STAR_SCROLLBAR.radius,
    '--scrollbar-thumb': palette.thumb,
    '--scrollbar-thumb-hover': palette.thumbHover,
  }
}

/**
 * star 模块路由跳转，自动透传 token / roomToken / softNameAbbr / reffer / theme / appVersion 等
 */
export function getStarInheritedQuery(
  query: RouteLocationNormalizedLoaded['query'] | Record<string, unknown>,
): Record<string, string> {
  const q = query as Record<string, unknown>
  const result: Record<string, string> = {}
  const pick = (key: string) => {
    const raw = q[key]
    if (raw == null || raw === '') return
    result[key] = String(Array.isArray(raw) ? raw[0] : raw)
  }

  pick('token')
  pick('roomToken')
  pick('softNameAbbr')
  pick('reffer')
  pick('theme')
  pick('userType')
  pick('os')

  const username = q.userName ?? q.username ?? q.usename
  if (username != null && username !== '') {
    result.username = String(Array.isArray(username) ? username[0] : username)
  }

  const appVersion = getStarAppVersion(q)
  if (appVersion) result.appVersion = appVersion

  return result
}

export function useStarRouter() {
  const router = useRouter()
  const route = useRoute()

  const inheritQuery = () => getStarInheritedQuery(route.query)

  const starPush = (path: string, extraQuery: Record<string, string> = {}) => {
    router.push({ path, query: { ...inheritQuery(), ...extraQuery } })
  }

  return { starPush }
}
